/*
 *     Copyright (C) 2021 TART K.K.
 *
 *     This program is free software: you can redistribute it and/or modify
 *     it under the terms of the GNU General Public License as published by
 *     the Free Software Foundation, either version 3 of the License, or
 *     (at your option) any later version.
 *
 *     This program is distributed in the hope that it will be useful,
 *     but WITHOUT ANY WARRANTY; without even the implied warranty of
 *     MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *     GNU General Public License for more details.
 *
 *     You should have received a copy of the GNU General Public License
 *     along with this program.  If not, see https://www.gnu.org/licenses/.
 */

import React, { useCallback, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  Button,
  TextField,
  Stack,
  useMediaQuery,
  Typography,
} from "@mui/material";
import themes from "../../../themes";
import styled from "@emotion/styled";
import { ethers } from "ethers";

export interface DonationDialogProps {
  readonly merkleProof: string[];
  readonly open: boolean;
  readonly setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  readonly claim: (...args: any[]) => Promise<void>;
  readonly getTokenId?: string | number;
}

const DonationDialog: React.FC<DonationDialogProps> = ({
  merkleProof,
  claim,
  ...props
}) => {
  const fullScreen = useMediaQuery("@media (max-width:340px)");
  const [donationInEther, setDonationInEther] = useState<number | undefined>();

  const onPurchaseButtonClick = useCallback(() => {
    claim(props.getTokenId, merkleProof, {
      value: ethers.utils.parseEther(donationInEther?.toString() ?? "0"),
    });
    props.setModalOpen(false);
  }, [claim, donationInEther, props.getTokenId]);

  const onCancelButtonClick = useCallback(() => {
    props.setModalOpen(false);
  }, [props.setModalOpen]);

  const content = () => {
    return (
      <>
        <StyledDialogTitle>NFT受け取り設定</StyledDialogTitle>
        <Box m={2}>
          <Typography>
            CHIMNEY TOWN DAOは、みなさんからの寄付を元手に活動します。
          </Typography>
          <TextField
            value={donationInEther}
            variant={"standard"}
            onChange={(e) => {
              if (!e.target.value) {
                setDonationInEther(undefined);
              } else {
                setDonationInEther(Number(e.target.value));
              }
            }}
            fullWidth
            size="small"
            helperText="寄付したい金額をETHで設定してください"
            inputProps={{ type: "number" }}
            placeholder="0.0"
          />
        </Box>
        <div>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ marginBottom: 1 }}
          >
            <Button
              color="primary"
              variant="outlined"
              fullWidth
              disableElevation
              onClick={onCancelButtonClick}
            >
              キャンセル
            </Button>
            <Button
              color="primary"
              variant="contained"
              fullWidth
              disableElevation
              onClick={onPurchaseButtonClick}
              style={{ color: "white" }}
            >
              NFTを受け取る
            </Button>
          </Stack>
        </div>
      </>
    );
  };

  return (
    <Dialog
      aria-labelledby="simple-dialog-title"
      open={props.open}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        square: true,
      }}
    >
      <Container>{content()}</Container>
    </Dialog>
  );
};

export default DonationDialog;

const Container = styled.div`
  color: #000;
  padding: ${themes.spacing(2)};
  border: 1px solid #01b9ff;
  @media (max-width: 340px) {
    border: none;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  padding: 0;
  text-align: center;
  text-transform: uppercase;
  color: #01b9ff;
`;

export const GridWrapper = styled.div`
  overflow: auto;
  max-height: 67vh;
  margin-bottom: 8px;
`;
